import { SectorAnalysis, RiskConfiguration } from "@/types/risk-analysis";
import {
  calculateRiskScore,
  recalculateAnalysisMetrics,
} from "./risk-calculator";

/**
 * Creates a new SectorAnalysis with only selected changes applied
 * Unchecked changes revert to original values
 *
 * @param originalAnalysis - The baseline analysis from database
 * @param currentAnalysis - The current analysis with all modifications
 * @param selectedChangeIds - Set of change IDs to apply (format: "impact-{categoryId}" or "probability-{categoryId}-{actorId}")
 * @param configuration - Risk configuration for score recalculation
 * @returns Modified SectorAnalysis with only selected changes
 */
export function createPartialAnalysis(
  originalAnalysis: SectorAnalysis,
  currentAnalysis: SectorAnalysis,
  selectedChangeIds: Set<string>,
  configuration: RiskConfiguration
): SectorAnalysis {
  // Start with original (database values)
  let updatedMatrix = originalAnalysis.attackMatrix.map((cat) => ({
    ...cat,
    actors: { ...cat.actors },
  }));

  // Apply only selected changes from current
  selectedChangeIds.forEach((changeId) => {
    const parts = changeId.split("-");
    const type = parts[0];
    const categoryId = parts[1];
    const actorId = parts[2]; // undefined for impact changes

    updatedMatrix = updatedMatrix.map((category) => {
      if (category.categoryId !== categoryId) return category;

      if (type === "impact") {
        const currentCategory = currentAnalysis.attackMatrix.find(
          (c) => c.categoryId === categoryId
        );
        if (!currentCategory) return category;

        // Recalculate all actor scores with new impact
        const updatedActors = Object.fromEntries(
          Object.entries(category.actors).map(([aId, actorData]) => [
            aId,
            {
              ...actorData,
              score: calculateRiskScore({
                probability: actorData.probability,
                impact: currentCategory.impact,
                attackType: category.tipAtac,
                orgSize: configuration.organizationSize,
              }),
            },
          ])
        );

        return {
          ...category,
          impact: currentCategory.impact,
          actors: updatedActors,
        };
      }

      if (type === "probability" && actorId) {
        const currentCategory = currentAnalysis.attackMatrix.find(
          (c) => c.categoryId === categoryId
        );
        if (!currentCategory) return category;

        const currentActor = currentCategory.actors[actorId];
        if (!currentActor) return category;

        const updatedActors = {
          ...category.actors,
          [actorId]: {
            probability: currentActor.probability,
            score: calculateRiskScore({
              probability: currentActor.probability,
              impact: category.impact,
              attackType: category.tipAtac,
              orgSize: configuration.organizationSize,
            }),
          },
        };

        return { ...category, actors: updatedActors };
      }

      return category;
    });
  });

  // Recalculate totals
  const { actorTotals, totalScore, cyfunLevel } =
    recalculateAnalysisMetrics(updatedMatrix);

  return {
    ...originalAnalysis,
    attackMatrix: updatedMatrix,
    actorTotals,
    totalScore,
    cyfunLevel,
  };
}
